import { api } from './client'
import type {
  ApiEstimate,
  StartEstimateRequest,
  UpdateEstimateStatusRequest,
  AddEstimateImagesRequest,
  EstimateDamageRequest,
  EstimateMajorOperationsRequest,
  MarkTotalLossRequest,
  UpdateLineItemsRequest,
} from './types'

export type { ApiEstimate }

export interface EstimateListParams {
  limit?: number
  offset?: number
}

// ── Deferral (req 2.4.x) ──
// Stored at estimate.metadata.deferral — the estimate keeps its live status
// (still an open opportunity), the marker just parks it out of follow-up
// pressure until a manager re-activates or confirms it dead.

export type EstimateDeferralAction = 'defer' | 'reactivate' | 'confirm_dead'

export interface EstimateDeferral {
  deferred: true
  deferredAt: string
  note?: string
}

/** Deferral marker from estimate.metadata, or null when not deferred. */
export function estimateDeferral(
  estimate: Pick<ApiEstimate, 'metadata'>,
): EstimateDeferral | null {
  const d = estimate.metadata?.deferral
  if (d && typeof d === 'object' && (d as EstimateDeferral).deferred === true) {
    return d as EstimateDeferral
  }
  return null
}

export function isDeferred(estimate: Pick<ApiEstimate, 'metadata'>): boolean {
  return estimateDeferral(estimate) !== null
}

/** "John S." — compact originator label for cards and list rows. */
export function estimateAuthorShort(
  estimate: Pick<ApiEstimate, 'createdBy'>,
): string | null {
  const a = estimate.createdBy
  if (!a) return null
  const lastInitial = a.lastName.trim().charAt(0)
  const name = lastInitial ? `${a.firstName} ${lastInitial}.` : a.firstName
  return name.trim() || null
}

export interface AgentJobResponse {
  estimate: ApiEstimate
  jobId: string
}

export const estimatesApi = {
  /** Start a full estimate for a team */
  startFull: (teamId: number | string, data: StartEstimateRequest) =>
    api.post<ApiEstimate>(`/estimates/team/${teamId}/full`, data),

  /** List estimates for a team */
  list: (teamId: number | string, params?: EstimateListParams) =>
    api.get<ApiEstimate[]>(`/estimates/team/${teamId}`, params),

  /** Get a single estimate */
  get: (estimateId: number | string) =>
    api.get<ApiEstimate>(`/estimates/${estimateId}`),

  /** Update estimate status */
  updateStatus: (estimateId: number | string, data: UpdateEstimateStatusRequest) =>
    api.patch<ApiEstimate>(`/estimates/${estimateId}/status`, data),

  /** Add/fix the customer's phone or email so follow-up can run.
   *  MOCK-ONLY — real BE has no customer-update endpoint (see design-log). */
  updateCustomer: (
    estimateId: number | string,
    data: { phone?: string; email?: string },
  ) => api.patch<ApiEstimate>(`/estimates/${estimateId}/customer`, data),

  /** Add images to an estimate */
  addImages: (estimateId: number | string, data: AddEstimateImagesRequest) =>
    api.post<ApiEstimate>(`/estimates/${estimateId}/images`, data),

  /** Run damage estimation agent - returns jobId for polling */
  runDamageEstimation: (estimateId: number | string, data?: EstimateDamageRequest) =>
    api.post<AgentJobResponse>(`/estimates/${estimateId}/damage`, data ?? {}),

  /** Run major operations agent - returns jobId for polling */
  runMajorOperations: (estimateId: number | string, data?: EstimateMajorOperationsRequest) =>
    api.post<AgentJobResponse>(`/estimates/${estimateId}/major-operations`, data ?? {}),

  /** Run cost estimation agent - returns jobId for polling */
  runCostEstimation: (estimateId: number | string) =>
    api.post<AgentJobResponse>(`/estimates/${estimateId}/cost`, {}),

  /** Re-derive line items + operations + pricing after the user edits line
   *  items. Full-estimate flow batches all edits then calls this once on
   *  Next; orchestrator covers what /cost would, so /cost is not chained. */
  runSectionsOrchestrator: (estimateId: number | string) =>
    api.post<AgentJobResponse>(`/estimates/${estimateId}/sections-orchestrator`, {}),

  /** Replace the estimate's line items in one batched PUT. Rows with `id`
   *  update in place; rows without `id` are created. Caller follows up
   *  with runSectionsOrchestrator to re-derive. */
  updateLineItems: (estimateId: number | string, data: UpdateLineItemsRequest) =>
    api.put<ApiEstimate>(`/estimates/${estimateId}/line-items`, data),

  /** Export an estimate as a PDF — BE returns a binary stream with Content-Disposition. */
  exportPdf: (estimateId: number | string) =>
    api.getBlob(`/estimates/${estimateId}/export/pdf`),

  /** POST /v1/estimates/:estimateId/total-loss — bodyshop estimator/GM only.
   *  Side effect: BE creates a SalesLead pinned to this estimate; the lead
   *  shows up in the dealership's /v1/sales/:teamId/leads/pending stream
   *  for follow-up by the sales team. */
  markAsTotalLoss: (estimateId: number | string, data?: MarkTotalLossRequest) =>
    api.post<ApiEstimate>(`/estimates/${estimateId}/total-loss`, data ?? {}),

  /** DELETE /v1/estimates/:estimateId/total-loss — bodyshop estimator/GM only.
   *  Reverses markAsTotalLoss: clears `totalLoss` and deletes the SalesLead. */
  unmarkTotalLoss: (estimateId: number | string) =>
    api.delete<ApiEstimate>(`/estimates/${estimateId}/total-loss`),

  /** PATCH /v1/estimates/:estimateId/deferral — mock-only, real BE endpoint
   *  pending. `defer` writes metadata.deferral (status unchanged);
   *  `reactivate` clears it; `confirm_dead` clears it AND declines the
   *  estimate (recording the same activity event as PATCH /status). */
  setDeferral: (estimateId: number | string, action: EstimateDeferralAction, note?: string) =>
    api.patch<ApiEstimate>(`/estimates/${estimateId}/deferral`, note ? { action, note } : { action }),
}
